'use client';

import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { createClient } from '@/lib/supabase/client';
import { vaultApiPath } from '@/lib/vault/api';
import { FREE_DOCUMENT_CAP, type VaultCategory, type VaultDocument } from '@/lib/vault/types';
import { getDriveAccessToken, type DriveAuthError } from '@/lib/google/auth';
import { uploadAppDataFile, downloadAppDataFile, deleteAppDataFile } from '@/lib/google/drive';
import { encryptBlob, decryptBlob } from '@/lib/vault/crypto';
import { writeLocal, readLocal, removeLocal } from '@/lib/vault/localStore';

export interface UploadDocumentInput {
  category: VaultCategory;
  title: string;
  file: File;
  expiryDate?: string;
  linkedCertificateId?: string | null;
  linkedRecordId?: string | null;
}

export interface DocumentPatch {
  category?: VaultCategory;
  title?: string;
  expiryDate?: string | null;
  linkedCertificateId?: string | null;
  linkedRecordId?: string | null;
}

interface MutationResult {
  ok: boolean;
  error?: string;
}

interface MigrationResult {
  migrated: number;
  failed: number;
  cleanupPending: number;
  error?: string;
}

interface VaultContextType {
  documents: VaultDocument[];
  loading: boolean;
  cap: number;
  count: number;
  atCap: boolean;
  isConfigured: boolean;
  /** Map of certificate id -> number of attached scans. */
  scanCountByCertificate: Record<string, number>;
  /** True when an Identity-category scan (passport/CDC) is stored. */
  hasIdentityScan: boolean;
  refresh: () => Promise<void>;
  uploadDocument: (input: UploadDocumentInput) => Promise<MutationResult>;
  updateDocument: (id: string, patch: DocumentPatch) => Promise<MutationResult>;
  deleteDocument: (id: string) => Promise<boolean>;
  deleteAll: () => Promise<boolean>;
  /** Server-signed URL valid for ttlSeconds (clamped server-side). Legacy docs only. */
  requestShareUrl: (id: string, ttlSeconds: number) => Promise<string | null>;
  /** Stage a decrypted Drive document copy and return a short-lived signed URL. */
  stageDriveShareUrl: (doc: VaultDocument, blob: Blob, ttlSeconds: number) => Promise<string | null>;
  /** Encrypt legacy Supabase-stored documents into Drive appData and switch their metadata rows. */
  migrateLegacyDocuments: () => Promise<MigrationResult>;
  /** Returns the decrypted file bytes for a document (Drive: local-first → Drive; legacy: signed URL). */
  getDocumentBlob: (doc: VaultDocument) => Promise<Blob>;
}

const noopAsync = async () => {};

const VaultContext = createContext<VaultContextType>({
  documents: [],
  loading: false,
  cap: FREE_DOCUMENT_CAP,
  count: 0,
  atCap: false,
  isConfigured: false,
  scanCountByCertificate: {},
  hasIdentityScan: false,
  refresh: noopAsync,
  uploadDocument: async () => ({ ok: false }),
  updateDocument: async () => ({ ok: false }),
  deleteDocument: async () => false,
  deleteAll: async () => false,
  requestShareUrl: async () => null,
  stageDriveShareUrl: async () => null,
  migrateLegacyDocuments: async () => ({ migrated: 0, failed: 0, cleanupPending: 0 }),
  getDocumentBlob: async () => {
    throw new Error('Vault is not configured.');
  },
});

export const useVault = () => useContext(VaultContext);

export function VaultProvider({ children }: { children: React.ReactNode }) {
  const { getAccessToken, isConfigured } = useAuth();
  const [documents, setDocuments] = useState<VaultDocument[]>([]);
  const [loading, setLoading] = useState(false);
  const [cap, setCap] = useState(FREE_DOCUMENT_CAP);

  const authHeader = useCallback(async (): Promise<Record<string, string>> => {
    const token = await getAccessToken();
    return token ? { Authorization: `Bearer ${token}` } : {};
  }, [getAccessToken]);

  const refresh = useCallback(async () => {
    if (!isConfigured) return;
    setLoading(true);
    try {
      const res = await fetch(vaultApiPath('/api/documents'), { headers: await authHeader() });
      const data = await res.json();
      if (res.ok) {
        setDocuments(data.documents || []);
        if (typeof data.cap === 'number') setCap(data.cap);
      }
    } catch {
      // Non-fatal; keep prior list.
    } finally {
      setLoading(false);
    }
  }, [isConfigured, authHeader]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  // New uploads are encrypted on-device and stored in the user's own Google
  // Drive appDataFolder (local-first copy on native). Drive must be connected;
  // file bytes never touch our servers — only metadata goes to Supabase.
  const uploadDocument = useCallback(async (input: UploadDocumentInput): Promise<MutationResult> => {
    const supabase = createClient();
    if (!supabase) return { ok: false, error: 'Supabase is not configured.' };

    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return { ok: false, error: 'Please sign in before adding documents.' };

    // Require a connected Drive (mints a short-lived access token).
    let token: string;
    try {
      token = await getDriveAccessToken();
    } catch (e) {
      const reconnect = (e as DriveAuthError)?.reconnect;
      return {
        ok: false,
        error: reconnect
          ? 'Google Drive needs reconnecting. Open Cloud backup and reconnect, then try again.'
          : 'Connect Google Drive (Cloud backup) to store documents.',
      };
    }

    const docId = (globalThis.crypto?.randomUUID?.() as string) || `${Date.now()}-${Math.random().toString(36).slice(2)}`;

    try {
      const { cipher, iv } = await encryptBlob(input.file);
      await writeLocal(docId, cipher); // native local-first cache; no-op on web

      let driveFileId: string;
      try {
        const driveFile = await uploadAppDataFile({
          token,
          name: `${docId}.enc`,
          data: cipher,
          mimeType: 'application/octet-stream',
          appProperties: { docId },
        });
        driveFileId = driveFile.id;
      } catch (e) {
        await removeLocal(docId);
        const detail = e instanceof Error ? e.message : String(e);
        console.warn('[Vault] Drive upload failed:', detail);
        return { ok: false, error: `Google Drive upload failed: ${detail}` };
      }

      const { error: insertError } = await (supabase as any).from('seafarer_documents').insert({
        id: docId,
        user_id: user.id,
        category: input.category,
        title: input.title.trim(),
        linked_record_id: input.linkedRecordId ?? null,
        linked_certificate_id: input.linkedCertificateId ?? null,
        file_name: input.file.name,
        file_path: null,
        file_type: input.file.type || null,
        file_size: input.file.size,
        expiry_date: input.expiryDate?.trim() || null,
        storage_kind: 'drive',
        drive_file_id: driveFileId,
        enc_iv: iv,
      });

      if (insertError) {
        // Roll back the Drive + local copies so we don't orphan bytes.
        try { await deleteAppDataFile(token, driveFileId); } catch { /* best effort */ }
        await removeLocal(docId);
        return { ok: false, error: insertError.message || 'Could not save the document.' };
      }

      await refresh();
      return { ok: true };
    } catch (e) {
      await removeLocal(docId);
      return { ok: false, error: e instanceof Error ? e.message : 'Could not encrypt or upload the document.' };
    }
  }, [refresh]);

  const updateDocument = useCallback(async (id: string, patch: DocumentPatch): Promise<MutationResult> => {
    const doc = documents.find((d) => d.id === id);
    // Drive rows are owned by the client (user RLS); patch metadata directly.
    if (doc?.storage_kind === 'drive') {
      const supabase = createClient();
      if (!supabase) return { ok: false, error: 'Supabase is not configured.' };
      const row: Record<string, unknown> = { updated_at: new Date().toISOString() };
      if (patch.category !== undefined) row.category = patch.category;
      if (patch.title !== undefined) row.title = patch.title;
      if (patch.expiryDate !== undefined) row.expiry_date = patch.expiryDate;
      if (patch.linkedCertificateId !== undefined) row.linked_certificate_id = patch.linkedCertificateId;
      if (patch.linkedRecordId !== undefined) row.linked_record_id = patch.linkedRecordId;
      const { error } = await (supabase as any).from('seafarer_documents').update(row).eq('id', id);
      if (error) return { ok: false, error: error.message || 'Could not update the document.' };
      setDocuments((prev) => prev.map((d) => (d.id === id ? { ...d, ...row } as VaultDocument : d)));
      return { ok: true };
    }

    try {
      const res = await fetch(vaultApiPath(`/api/documents?id=${encodeURIComponent(id)}`), {
        method: 'PATCH',
        headers: { ...(await authHeader()), 'content-type': 'application/json' },
        body: JSON.stringify(patch),
      });
      const data = await res.json();
      if (!res.ok) return { ok: false, error: data.error || 'Could not update the document.' };
      setDocuments((prev) => prev.map((d) => (d.id === id ? { ...d, ...(data.document || {}), signedUrl: d.signedUrl } : d)));
      return { ok: true };
    } catch {
      return { ok: false, error: 'Network error. Please try again.' };
    }
  }, [authHeader, documents]);

  const deleteDocument = useCallback(async (id: string): Promise<boolean> => {
    const doc = documents.find((d) => d.id === id);
    if (doc?.storage_kind === 'drive') {
      // Remove Drive file first so we never orphan bytes in the user's appData.
      // 404 = already gone (safe to proceed); any other error blocks the delete
      // to prevent leaving an unreachable encrypted file in Drive.
      if (doc.drive_file_id) {
        try {
          const token = await getDriveAccessToken();
          await deleteAppDataFile(token, doc.drive_file_id);
        } catch (e) {
          const status = (e as { status?: number })?.status;
          if (status !== 404) return false;
        }
      }
      await removeLocal(id);
      const supabase = createClient();
      if (supabase) {
        const { error } = await (supabase as any).from('seafarer_documents').delete().eq('id', id);
        if (error) return false;
      }
      setDocuments((prev) => prev.filter((d) => d.id !== id));
      return true;
    }

    try {
      const res = await fetch(vaultApiPath(`/api/documents?id=${encodeURIComponent(id)}`), {
        method: 'DELETE',
        headers: await authHeader(),
      });
      if (res.ok) {
        setDocuments((prev) => prev.filter((d) => d.id !== id));
        return true;
      }
      return false;
    } catch {
      return false;
    }
  }, [authHeader, documents]);

  const deleteAll = useCallback(async (): Promise<boolean> => {
    // Drive files must be deleted before their metadata rows. If we can't confirm
    // each Drive file is gone (or already missing — 404), abort early so we don't
    // orphan encrypted bytes in the user's appData.
    const driveDocs = documents.filter((d) => d.storage_kind === 'drive');
    if (driveDocs.length > 0) {
      let token: string;
      try { token = await getDriveAccessToken(); } catch { return false; }
      for (const d of driveDocs) {
        if (d.drive_file_id) {
          try { await deleteAppDataFile(token, d.drive_file_id); } catch (e) {
            const status = (e as { status?: number })?.status;
            if (status !== 404) return false;
          }
        }
        await removeLocal(d.id);
      }
    }
    try {
      const res = await fetch(vaultApiPath('/api/documents?all=1'), {
        method: 'DELETE',
        headers: await authHeader(),
      });
      if (res.ok) {
        setDocuments([]);
        return true;
      }
      return false;
    } catch {
      return false;
    }
  }, [authHeader, documents]);

  const requestShareUrl = useCallback(async (id: string, ttlSeconds: number): Promise<string | null> => {
    try {
      const res = await fetch(
        vaultApiPath(`/api/documents?share=${encodeURIComponent(id)}&ttl=${Math.round(ttlSeconds)}`),
        { headers: await authHeader() },
      );
      const data = await res.json();
      return res.ok ? (data.url ?? null) : null;
    } catch {
      return null;
    }
  }, [authHeader]);

  const stageDriveShareUrl = useCallback(async (doc: VaultDocument, blob: Blob, ttlSeconds: number): Promise<string | null> => {
    try {
      const form = new FormData();
      form.append('action', 'stage-share');
      form.append('documentId', doc.id);
      form.append('ttl', String(Math.round(ttlSeconds)));
      form.append('file', new File([blob], doc.file_name || doc.title || 'document', {
        type: doc.file_type || blob.type || 'application/octet-stream',
      }));
      const res = await fetch(vaultApiPath('/api/documents'), {
        method: 'POST',
        headers: await authHeader(),
        body: form,
      });
      const data = await res.json();
      return res.ok ? (data.url ?? null) : null;
    } catch {
      return null;
    }
  }, [authHeader]);

  // Returns decrypted file bytes. Drive docs: device cache first, else download
  // from Drive (and re-cache on native) then decrypt. Legacy docs: signed URL.
  const getDocumentBlob = useCallback(async (doc: VaultDocument): Promise<Blob> => {
    if (doc.storage_kind === 'drive') {
      if (!doc.enc_iv) throw new Error('This document is missing its encryption details.');
      const cached = await readLocal(doc.id);
      if (cached) return decryptBlob(cached, doc.enc_iv, doc.file_type);
      if (!doc.drive_file_id) throw new Error('This document is missing its Drive reference.');
      const token = await getDriveAccessToken();
      const cipher = await downloadAppDataFile(token, doc.drive_file_id);
      await writeLocal(doc.id, cipher); // re-cache on native
      return decryptBlob(cipher, doc.enc_iv, doc.file_type);
    }
    // Legacy Supabase-stored document.
    if (!doc.signedUrl) throw new Error('This document has no available file URL.');
    const res = await fetch(doc.signedUrl);
    if (!res.ok) throw new Error('Could not fetch the document.');
    return res.blob();
  }, []);

  const migrateLegacyDocuments = useCallback(async (): Promise<MigrationResult> => {
    const legacyDocs = documents.filter((d) => (d.storage_kind ?? 'supabase') !== 'drive' || !!d.file_path);
    if (legacyDocs.length === 0) return { migrated: 0, failed: 0, cleanupPending: 0 };

    const supabase = createClient();
    if (!supabase) return { migrated: 0, failed: legacyDocs.length, cleanupPending: 0, error: 'Supabase is not configured.' };

    let token: string;
    try {
      token = await getDriveAccessToken();
    } catch {
      return { migrated: 0, failed: legacyDocs.length, cleanupPending: 0, error: 'Connect Google Drive before migrating legacy documents.' };
    }

    let migrated = 0;
    let failed = 0;
    let cleanupPending = 0;

    for (const doc of legacyDocs) {
      if (!doc.file_path) {
        failed += 1;
        continue;
      }

      let driveFileId: string | null = null;
      try {
        if (doc.storage_kind === 'drive') {
          const { error: removeError } = await supabase.storage
            .from('seafarer-documents')
            .remove([doc.file_path]);
          if (removeError) {
            cleanupPending += 1;
          } else {
            await (supabase as any)
              .from('seafarer_documents')
              .update({ file_path: null, updated_at: new Date().toISOString() })
              .eq('id', doc.id);
          }
          continue;
        }

        if (!doc.signedUrl) {
          failed += 1;
          continue;
        }

        const plain = await getDocumentBlob(doc);
        const { cipher, iv } = await encryptBlob(plain);
        await writeLocal(doc.id, cipher);
        const driveFile = await uploadAppDataFile({
          token,
          name: `${doc.id}.enc`,
          data: cipher,
          mimeType: 'application/octet-stream',
          appProperties: { docId: doc.id, migratedFrom: 'supabase' },
        });
        driveFileId = driveFile.id;

        const { error: updateError } = await (supabase as any)
          .from('seafarer_documents')
          .update({
            storage_kind: 'drive',
            drive_file_id: driveFileId,
            enc_iv: iv,
            enc_algo: 'AES-GCM',
            updated_at: new Date().toISOString(),
          })
          .eq('id', doc.id);

        if (updateError) {
          try { await deleteAppDataFile(token, driveFileId); } catch { /* best effort */ }
          await removeLocal(doc.id);
          failed += 1;
          continue;
        }

        const { error: removeError } = await supabase.storage
          .from('seafarer-documents')
          .remove([doc.file_path]);

        if (removeError) {
          cleanupPending += 1;
        } else {
          await (supabase as any)
            .from('seafarer_documents')
            .update({ file_path: null, updated_at: new Date().toISOString() })
            .eq('id', doc.id);
        }

        migrated += 1;
      } catch {
        if (driveFileId) {
          try { await deleteAppDataFile(token, driveFileId); } catch { /* best effort */ }
        }
        await removeLocal(doc.id);
        failed += 1;
      }
    }

    await refresh();
    return { migrated, failed, cleanupPending };
  }, [documents, getDocumentBlob, refresh]);

  const scanCountByCertificate = useMemo(() => {
    const map: Record<string, number> = {};
    for (const doc of documents) {
      if (doc.linked_certificate_id) {
        map[doc.linked_certificate_id] = (map[doc.linked_certificate_id] || 0) + 1;
      }
    }
    return map;
  }, [documents]);

  const hasIdentityScan = useMemo(
    () => documents.some((d) => d.category === 'identity'),
    [documents],
  );

  const value = useMemo<VaultContextType>(
    () => ({
      documents,
      loading,
      cap,
      count: documents.length,
      // No document cap — storage is the user's device + their own Google Drive.
      atCap: false,
      isConfigured,
      scanCountByCertificate,
      hasIdentityScan,
      refresh,
      uploadDocument,
      updateDocument,
      deleteDocument,
      deleteAll,
      requestShareUrl,
      stageDriveShareUrl,
      migrateLegacyDocuments,
      getDocumentBlob,
    }),
    [documents, loading, cap, isConfigured, scanCountByCertificate, hasIdentityScan, refresh, uploadDocument, updateDocument, deleteDocument, deleteAll, requestShareUrl, stageDriveShareUrl, migrateLegacyDocuments, getDocumentBlob],
  );

  return <VaultContext.Provider value={value}>{children}</VaultContext.Provider>;
}
